/**
 * Minimal TrueType (glyf-flavored) parser — just enough to turn text into
 * closed outline polygons for sketch profiles: sfnt directory, head, maxp,
 * cmap (formats 4 + 12), loca, glyf (simple + composite), hhea/hmtx for
 * advances. Quadratic beziers are sampled to polylines; TrueType's implied
 * on-curve midpoints are handled. No hinting, kerning, or variations (a
 * variable font renders at its default instance).
 */

export interface ParsedFont {
  unitsPerEm: number;
  glyphCount: number;
  /** char code → glyph id */
  glyphId(code: number): number;
  /** glyph id → closed contours in font units (y up) */
  outline(glyph: number): { x: number; y: number }[][];
  /** glyph id → horizontal advance in font units */
  advance(glyph: number): number;
}

const TAG = (dv: DataView, off: number): string =>
  String.fromCharCode(dv.getUint8(off), dv.getUint8(off + 1), dv.getUint8(off + 2), dv.getUint8(off + 3));

interface GlyfPoint {
  x: number;
  y: number;
  on: boolean;
}

const BEZIER_STEPS = 8;

export function parseTtf(buffer: ArrayBuffer): ParsedFont {
  const dv = new DataView(buffer);
  if (dv.getUint32(0) !== 0x00010000 && TAG(dv, 0) !== 'true') {
    throw new Error('Not a TrueType (glyf) font');
  }
  const numTables = dv.getUint16(4);
  const tables = new Map<string, { off: number; len: number }>();
  for (let i = 0; i < numTables; i++) {
    const rec = 12 + i * 16;
    tables.set(TAG(dv, rec), { off: dv.getUint32(rec + 8), len: dv.getUint32(rec + 12) });
  }
  const need = (tag: string): { off: number; len: number } => {
    const t = tables.get(tag);
    if (!t) throw new Error(`Font missing ${tag} table`);
    return t;
  };

  const head = need('head').off;
  const unitsPerEm = dv.getUint16(head + 18);
  const longLoca = dv.getInt16(head + 50) === 1;
  const glyphCount = dv.getUint16(need('maxp').off + 4);

  // --- cmap: prefer format 12, else format 4 -------------------------------
  const cmap = need('cmap').off;
  let sub4 = -1;
  let sub12 = -1;
  const cmapCount = dv.getUint16(cmap + 2);
  for (let i = 0; i < cmapCount; i++) {
    const rec = cmap + 4 + i * 8;
    const platform = dv.getUint16(rec);
    const encoding = dv.getUint16(rec + 2);
    const off = cmap + dv.getUint32(rec + 4);
    const format = dv.getUint16(off);
    const unicodeish =
      platform === 0 || (platform === 3 && (encoding === 1 || encoding === 10));
    if (!unicodeish) continue;
    if (format === 12) sub12 = off;
    if (format === 4 && sub4 < 0) sub4 = off;
  }
  if (sub4 < 0 && sub12 < 0) throw new Error('Font has no usable cmap');

  const glyphId = (code: number): number => {
    if (sub12 >= 0) {
      const nGroups = dv.getUint32(sub12 + 12);
      let lo = 0, hi = nGroups - 1;
      while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        const g = sub12 + 16 + mid * 12;
        const start = dv.getUint32(g);
        const end = dv.getUint32(g + 4);
        if (code < start) hi = mid - 1;
        else if (code > end) lo = mid + 1;
        else return dv.getUint32(g + 8) + (code - start);
      }
      return 0;
    }
    const segX2 = dv.getUint16(sub4 + 6);
    const endBase = sub4 + 14;
    const startBase = endBase + segX2 + 2;
    const deltaBase = startBase + segX2;
    const rangeBase = deltaBase + segX2;
    for (let seg = 0; seg < segX2 / 2; seg++) {
      if (code <= dv.getUint16(endBase + seg * 2)) {
        const start = dv.getUint16(startBase + seg * 2);
        if (code < start) return 0;
        const delta = dv.getInt16(deltaBase + seg * 2);
        const rangeOff = dv.getUint16(rangeBase + seg * 2);
        if (rangeOff === 0) return (code + delta) & 0xffff;
        const idx = rangeBase + seg * 2 + rangeOff + (code - start) * 2;
        const g = dv.getUint16(idx);
        return g === 0 ? 0 : (g + delta) & 0xffff;
      }
    }
    return 0;
  };

  // --- hmtx ---------------------------------------------------------------
  const numHMetrics = dv.getUint16(need('hhea').off + 34);
  const hmtx = need('hmtx').off;
  const advance = (glyph: number): number =>
    glyph < numHMetrics
      ? dv.getUint16(hmtx + glyph * 4)
      : dv.getUint16(hmtx + (numHMetrics - 1) * 4);

  // --- loca / glyf ----------------------------------------------------------
  const loca = need('loca').off;
  const glyf = need('glyf').off;
  const glyphOffset = (g: number): [number, number] => {
    if (longLoca) return [dv.getUint32(loca + g * 4), dv.getUint32(loca + g * 4 + 4)];
    return [dv.getUint16(loca + g * 2) * 2, dv.getUint16(loca + g * 2 + 2) * 2];
  };

  function simplePoints(off: number, nContours: number): { pts: GlyfPoint[]; ends: number[] } {
    const ends: number[] = [];
    for (let i = 0; i < nContours; i++) ends.push(dv.getUint16(off + 10 + i * 2));
    const nPts = ends[nContours - 1]! + 1;
    let p = off + 10 + nContours * 2;
    p += 2 + dv.getUint16(p); // instructions
    // Flags (with repeat runs).
    const flags: number[] = [];
    while (flags.length < nPts) {
      const f = dv.getUint8(p++);
      flags.push(f);
      if (f & 8) {
        let rep = dv.getUint8(p++);
        while (rep-- > 0) flags.push(f);
      }
    }
    // Deltas.
    const pts: GlyfPoint[] = [];
    let x = 0;
    for (let i = 0; i < nPts; i++) {
      const f = flags[i]!;
      if (f & 2) {
        const d = dv.getUint8(p++);
        x += f & 16 ? d : -d;
      } else if (!(f & 16)) {
        x += dv.getInt16(p);
        p += 2;
      }
      pts.push({ x, y: 0, on: (f & 1) !== 0 });
    }
    let y = 0;
    for (let i = 0; i < nPts; i++) {
      const f = flags[i]!;
      if (f & 4) {
        const d = dv.getUint8(p++);
        y += f & 32 ? d : -d;
      } else if (!(f & 32)) {
        y += dv.getInt16(p);
        p += 2;
      }
      pts[i]!.y = y;
    }
    return { pts, ends };
  }

  /** Contour of on/off points → sampled closed polyline. */
  function contourToPolyline(contour: GlyfPoint[]): { x: number; y: number }[] {
    if (contour.length === 0) return [];
    // Normalize: expand implied on-curve midpoints between OFF pairs, and
    // rotate so we start on an on-curve point.
    const expanded: GlyfPoint[] = [];
    for (let i = 0; i < contour.length; i++) {
      const cur = contour[i]!;
      const next = contour[(i + 1) % contour.length]!;
      expanded.push(cur);
      if (!cur.on && !next.on) {
        expanded.push({ x: (cur.x + next.x) / 2, y: (cur.y + next.y) / 2, on: true });
      }
    }
    let startIdx = expanded.findIndex((q) => q.on);
    if (startIdx < 0) {
      // All off-curve (rare): synthesize a start midpoint.
      const a = expanded[0]!, b = expanded[1] ?? expanded[0]!;
      expanded.unshift({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2, on: true });
      startIdx = 0;
    }
    const out: { x: number; y: number }[] = [];
    const n = expanded.length;
    let i = startIdx;
    out.push({ x: expanded[i]!.x, y: expanded[i]!.y });
    let steps = 0;
    while (steps < n) {
      const cur = expanded[(i) % n]!;
      const next = expanded[(i + 1) % n]!;
      if (next.on) {
        out.push({ x: next.x, y: next.y });
        i += 1;
        steps += 1;
      } else {
        const end = expanded[(i + 2) % n]!; // on-curve (normalized above)
        for (let k = 1; k <= BEZIER_STEPS; k++) {
          const t = k / BEZIER_STEPS;
          const mt = 1 - t;
          out.push({
            x: mt * mt * cur.x + 2 * mt * t * next.x + t * t * end.x,
            y: mt * mt * cur.y + 2 * mt * t * next.y + t * t * end.y,
          });
        }
        i += 2;
        steps += 2;
      }
    }
    out.pop(); // closing point duplicates the start
    return out;
  }

  function outline(glyph: number, depth = 0): { x: number; y: number }[][] {
    if (glyph >= glyphCount || depth > 5) return [];
    const [start, end] = glyphOffset(glyph);
    if (end <= start) return []; // empty glyph (space)
    const off = glyf + start;
    const nContours = dv.getInt16(off);

    if (nContours >= 0) {
      const { pts, ends } = simplePoints(off, nContours);
      const loops: { x: number; y: number }[][] = [];
      let from = 0;
      for (const to of ends) {
        const poly = contourToPolyline(pts.slice(from, to + 1));
        if (poly.length >= 3) loops.push(poly);
        from = to + 1;
      }
      return loops;
    }

    // Composite glyph: accumulate transformed component outlines.
    const loops: { x: number; y: number }[][] = [];
    let p = off + 10;
    for (;;) {
      const flags = dv.getUint16(p);
      const compGlyph = dv.getUint16(p + 2);
      p += 4;
      let dx = 0, dy = 0;
      if (flags & 1) {
        dx = dv.getInt16(p);
        dy = dv.getInt16(p + 2);
        p += 4;
      } else {
        dx = dv.getInt8(p);
        dy = dv.getInt8(p + 1);
        p += 2;
      }
      let a = 1, b = 0, c = 0, d = 1;
      if (flags & 8) {
        a = d = dv.getInt16(p) / 16384;
        p += 2;
      } else if (flags & 64) {
        a = dv.getInt16(p) / 16384;
        d = dv.getInt16(p + 2) / 16384;
        p += 4;
      } else if (flags & 128) {
        a = dv.getInt16(p) / 16384;
        b = dv.getInt16(p + 2) / 16384;
        c = dv.getInt16(p + 4) / 16384;
        d = dv.getInt16(p + 6) / 16384;
        p += 8;
      }
      for (const loop of outline(compGlyph, depth + 1)) {
        loops.push(loop.map((q) => ({ x: a * q.x + c * q.y + dx, y: b * q.x + d * q.y + dy })));
      }
      if (!(flags & 32)) break; // MORE_COMPONENTS
    }
    return loops;
  }

  return { unitsPerEm, glyphCount, glyphId, outline, advance };
}
